'use client';

import { useState, useMemo } from 'react';
import useSWR from 'swr';
import { useAuth } from '@/lib/integrations/supabase';
import { getAdminContextHeaders } from '@/lib/features/swr';
import { Button } from '@/components/shared/ui/button';
import { Skeleton } from '@/components/shared/ui/skeleton';
import {
  Building2,
  AlertTriangle,
  ArrowUpRight,
  RefreshCw,
} from 'lucide-react';
import dynamic from 'next/dynamic';
import { cn } from '@/lib/utils';

// Components
import { DashboardFilterBar, type FilterState } from './dashboard-filter-bar';
import { AlertsPanel } from './alerts-panel';
import { BookingPipeline } from './booking-pipeline';
import { ActivityFeed } from './activity-feed';
import { HourlyHeatmap } from './hourly-heatmap';

// Lazy load recharts
const BarChart = dynamic(() => import('recharts').then(mod => mod.BarChart), { ssr: false });
const Bar = dynamic(() => import('recharts').then(mod => mod.Bar), { ssr: false });
const PieChart = dynamic(() => import('recharts').then(mod => mod.PieChart), { ssr: false });
const Pie = dynamic(() => import('recharts').then(mod => mod.Pie), { ssr: false });
const Cell = dynamic(() => import('recharts').then(mod => mod.Cell), { ssr: false });
const ResponsiveContainer = dynamic(() => import('recharts').then(mod => mod.ResponsiveContainer), { ssr: false });
const Tooltip = dynamic(() => import('recharts').then(mod => mod.Tooltip), { ssr: false });
const XAxis = dynamic(() => import('recharts').then(mod => mod.XAxis), { ssr: false });
const YAxis = dynamic(() => import('recharts').then(mod => mod.YAxis), { ssr: false });

// Types
import type {
  ShopHealthMetrics,
  WalkInPerformanceMetrics,
  RevenueEfficiencyMetrics,
  HourlyLoadMetrics,
  StaffDetailedMetrics,
  DashboardAlert,
} from '@/lib/integrations/supabase/types';

interface BookingAnalytics {
  total: number;
  walkIns: number;
  completed: number;
  cancelled: number;
  noShow: number;
  pending: number;
  inProgress: number;
}

interface RevenueAnalytics {
  total: number;
  cash: number;
  card: number;
  transfer: number;
  completedBookings: number;
  averageTicket: number;
}

interface CustomerAnalytics {
  total: number;
  newInPeriod: number;
  returningRate: number;
  returningCount?: number;
}

interface TodayAnalytics {
  bookings: number;
  completed: number;
  walkIns: number;
  revenue: number;
  pending: number;
}

interface AllTimeAnalytics {
  bookings: number;
  revenue: number;
  customers: number;
}

interface QueueAnalytics {
  active: boolean;
  waiting: number;
  serving: number;
  completed: number;
  total: number;
}

interface StaffMemberAnalytics {
  id: string;
  name: string;
  role: string;
  isActive: boolean;
  completedBookings: number;
  revenue: number;
}

interface ServiceAnalytics {
  id: string;
  name: string;
  bookingsCount: number;
  revenue: number;
  averageDuration: number;
}

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  minQuantity: number;
  isLowStock: boolean;
}

interface BusinessDetails {
  id: string;
  name: string;
  logo_url: string | null;
  owner_name: string | null;
  created_at: string;
}

interface BusinessAnalytics {
  business?: BusinessDetails;
  bookings?: BookingAnalytics;
  revenue?: RevenueAnalytics;
  customers?: CustomerAnalytics;
  staff?: StaffMemberAnalytics[];
  services?: ServiceAnalytics[];
  inventory?: InventoryItem[];
  today?: TodayAnalytics;
  allTime?: AllTimeAnalytics;
  queue?: QueueAnalytics;
  // Extended metrics
  shopHealth?: ShopHealthMetrics;
  walkInPerformance?: WalkInPerformanceMetrics;
  revenueEfficiency?: RevenueEfficiencyMetrics;
  hourlyLoad?: HourlyLoadMetrics;
  staffDetailed?: StaffDetailedMetrics[];
  alerts?: DashboardAlert[];
}

// Chart colors
const COLORS = ['#18181b', '#52525b', '#a1a1aa', '#d4d4d8'];

// SWR Fetcher
const fetcher = async (url: string) => {
  const res = await fetch(url, {
    headers: {
      ...getAdminContextHeaders(),
    },
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || 'Failed to fetch');
  }
  return res.json();
};

export function MonitoringDashboard() {
  const { effectiveBusinessId } = useAuth();
  const [filters, setFilters] = useState<FilterState>({
    dateRange: '30d',
    staffId: null,
    serviceId: null,
    channel: 'all',
    status: 'all',
  });

  const { data, isLoading, error, mutate } = useSWR(
    effectiveBusinessId
      ? `/api/admin/analytics?type=comprehensive&businessId=${effectiveBusinessId}`
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 30000,
    }
  );

  const analytics: BusinessAnalytics | null = data?.analytics || null;

  const formatCurrency = (amount: number) => {
    return `AED ${amount.toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Extract data
  const bookings = analytics?.bookings;
  const revenue = analytics?.revenue;
  const customers = analytics?.customers;
  const staff = analytics?.staff || [];
  const services = analytics?.services || [];
  const inventory = analytics?.inventory || [];
  const today = analytics?.today;
  const queue = analytics?.queue;
  const shopHealth = analytics?.shopHealth;
  const walkInPerformance = analytics?.walkInPerformance;
  const revenueEfficiency = analytics?.revenueEfficiency;
  const hourlyLoad = analytics?.hourlyLoad;
  const staffDetailed = analytics?.staffDetailed || [];
  const alerts = analytics?.alerts || [];

  const lowStockItems = inventory.filter(item => item.isLowStock);
  const activeStaff = staff.filter(s => s.isActive);
  const completionRate = bookings?.total ? Math.round((bookings.completed / bookings.total) * 100) : 0;

  // Prepare filter options
  const staffOptions = useMemo(() =>
    staff.map(s => ({ id: s.id, name: s.name })),
    [staff]
  );

  const serviceOptions = useMemo(() =>
    services.map(s => ({ id: s.id, name: s.name })),
    [services]
  );

  // No business selected
  if (!effectiveBusinessId) {
    return (
      <div className="p-3 sm:p-4 lg:p-6">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Building2 className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">No Business Selected</h2>
          <p className="text-base text-muted-foreground max-w-md mb-6">
            Select a business from the home page to view the monitoring dashboard.
          </p>
          <Button variant="outline" onClick={() => window.location.href = '/home'} className="gap-2">
            <ArrowUpRight className="h-4 w-4" />
            Go to Business Selector
          </Button>
        </div>
      </div>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <div className="p-3 sm:p-4 lg:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-9 w-[300px]" />
        </div>
        <Skeleton className="h-10 w-full rounded-lg" />
        <div className="grid grid-cols-4 lg:grid-cols-8 gap-3">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-[300px] rounded-xl" />
          <Skeleton className="h-[300px] rounded-xl" />
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="p-3 sm:p-4 lg:p-6">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Error Loading Dashboard</h2>
          <p className="text-base text-muted-foreground max-w-md mb-6">{error.message}</p>
          <Button onClick={() => mutate()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Walk-in chart data
  const walkInChartData = walkInPerformance?.walkInsByHour || [];

  // Payment method data
  const paymentData = [
    { name: 'Cash', value: revenue?.cash || 0 },
    { name: 'Card', value: revenue?.card || 0 },
    { name: 'Transfer', value: revenue?.transfer || 0 },
  ].filter(d => d.value > 0);

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4">
      {/* ==================== HEADER ==================== */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-foreground">
            {analytics?.business?.name || 'Monitoring Dashboard'}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Operations Dashboard
          </p>
        </div>

        {/* Shop Status Badge */}
        {shopHealth && (
          <div className="flex items-center gap-3">
            <div className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium',
              shopHealth.isOpen
                ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                : 'bg-muted text-muted-foreground'
            )}>
              <div className={cn(
                'w-2 h-2 rounded-full',
                shopHealth.isOpen ? 'bg-emerald-500' : 'bg-muted-foreground/50'
              )} />
              {shopHealth.openStatus === 'peak' ? 'Peak Hours' :
               shopHealth.openStatus === 'open' ? 'Open' : 'Closed'}
            </div>
          </div>
        )}
      </div>

      {/* ==================== FILTER BAR ==================== */}
      <DashboardFilterBar
        staff={staffOptions}
        services={serviceOptions}
        filters={filters}
        onFilterChange={setFilters}
        onRefresh={() => mutate()}
        isLoading={isLoading}
      />

      {/* ==================== ROW 1: OPERATIONS SUMMARY (KPIs + Pipeline combined) ==================== */}
      <BookingPipeline
        created={bookings?.total || 0}
        confirmed={(bookings?.pending || 0) + (bookings?.inProgress || 0)}
        inProgress={bookings?.inProgress || 0}
        completed={bookings?.completed || 0}
        cancelled={bookings?.cancelled || 0}
        noShow={bookings?.noShow || 0}
        revenue={today?.revenue || revenue?.total || 0}
        walkIns={today?.walkIns || bookings?.walkIns || 0}
        customers={customers?.total || 0}
        avgTicket={revenueEfficiency?.avgTicketSize || revenue?.averageTicket || 0}
        utilization={shopHealth?.utilizationToday || 0}
        activeBarbersNow={shopHealth?.activeBarbersNow || activeStaff.length}
        totalChairs={shopHealth?.totalChairs || staff.length}
        queueLength={shopHealth?.queueLength || queue?.waiting || 0}
        avgWaitTime={shopHealth?.avgWaitTimeMinutes || 0}
        formatCurrency={formatCurrency}
      />

      {/* ==================== ROW 2: WALK-INS + ALERTS + ACTIVITY (3 columns) ==================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Walk-in Bookings List */}
        <div className="card-bookdesk !p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Today&apos;s Walk-ins</h3>
              <span className="text-sm text-muted-foreground">
                {walkInPerformance?.walkInsToday || today?.walkIns || bookings?.walkIns || 0} total
              </span>
            </div>
          </div>
          {(walkInPerformance?.walkInBookings?.length || 0) > 0 ? (
            <div className="divide-y divide-border max-h-[280px] overflow-y-auto">
              {walkInPerformance?.walkInBookings?.slice(0, 10).map((walkIn) => (
                <div key={walkIn.id} className="px-4 py-3 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium truncate">{walkIn.customerName}</p>
                    <span className="text-sm text-muted-foreground">{walkIn.time}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground truncate flex-1">
                      {walkIn.services} • {walkIn.staffName}
                    </p>
                    <span className={cn(
                      'text-xs px-2 py-0.5 rounded font-medium',
                      walkIn.status === 'completed' ? 'bg-muted text-foreground' :
                      walkIn.status === 'in_progress' ? 'bg-muted text-foreground' :
                      walkIn.status === 'cancelled' ? 'bg-muted text-muted-foreground' :
                      'bg-muted text-muted-foreground'
                    )}>
                      {walkIn.status === 'in_progress' ? 'In Progress' :
                       walkIn.status.charAt(0).toUpperCase() + walkIn.status.slice(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-3xl font-bold tabular-nums">{today?.walkIns || bookings?.walkIns || 0}</p>
              <p className="text-sm text-muted-foreground mt-1">walk-ins today</p>
              {(today?.walkIns || bookings?.walkIns || 0) === 0 && (
                <p className="text-xs text-muted-foreground mt-2">No walk-ins yet</p>
              )}
            </div>
          )}
        </div>

        {/* Alerts Panel */}
        <AlertsPanel
          alerts={alerts}
          onAlertClick={() => {}}
          onDismiss={() => {}}
        />

        {/* Activity Feed */}
        <ActivityFeed
          businessId={effectiveBusinessId}
          refreshInterval={60000}
          maxItems={10}
        />
      </div>

      {/* ==================== ROW 3: HOURLY HEATMAP ==================== */}
      {hourlyLoad && hourlyLoad.todayHourlyLoad.length > 0 && (
        <HourlyHeatmap
          todayHourlyLoad={hourlyLoad.todayHourlyLoad}
          weeklyHeatmap={hourlyLoad.weeklyHeatmap}
          showRevenue
        />
      )}

      {/* ==================== ROW 4: STAFF + SERVICES + REVENUE (3 columns) ==================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Staff Performance */}
        <div className="card-bookdesk !p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Staff Performance</h3>
              <span className="text-sm text-muted-foreground">{staffDetailed.length} barbers</span>
            </div>
          </div>
          {staffDetailed.length > 0 ? (
            <div className="divide-y divide-border max-h-[320px] overflow-y-auto">
              {staffDetailed.slice(0, 8).map((member) => (
                <div key={member.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-semibold">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{member.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {member.customersServedToday} served • {member.utilization}% utilization
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold tabular-nums">{formatCurrency(member.revenueToday)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-muted-foreground">No staff data</div>
          )}
        </div>

        {/* Services Table */}
        <div className="card-bookdesk !p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Services</h3>
              <span className="text-sm text-muted-foreground">{services.length} total</span>
            </div>
          </div>
          {services.length > 0 ? (
            <div className="divide-y divide-border max-h-[320px] overflow-y-auto">
              {services.slice(0, 8).map(service => (
                <div key={service.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{service.name}</p>
                    <p className="text-xs text-muted-foreground">{service.bookingsCount} bookings • {service.averageDuration} min</p>
                  </div>
                  <p className="text-sm font-semibold tabular-nums">{formatCurrency(service.revenue)}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-muted-foreground">No services</div>
          )}
        </div>

        {/* Revenue Breakdown */}
        <div className="card-bookdesk !p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Revenue Breakdown</h3>
              <span className="text-sm text-muted-foreground">{formatCurrency(revenue?.total || 0)} total</span>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-4">
              {paymentData.length > 0 ? (
                <>
                  <div className="w-24 h-24 flex-shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={paymentData}
                          cx="50%"
                          cy="50%"
                          innerRadius={28}
                          outerRadius={42}
                          dataKey="value"
                          strokeWidth={0}
                        >
                          {paymentData.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-2">
                    {[
                      { label: 'Cash', value: revenue?.cash || 0 },
                      { label: 'Card', value: revenue?.card || 0 },
                      { label: 'Transfer', value: revenue?.transfer || 0 },
                    ].map((item, i) => (
                      <div key={item.label} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i] }} />
                        <span className="text-sm text-muted-foreground flex-1">{item.label}</span>
                        <span className="text-sm font-semibold tabular-nums">{formatCurrency(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex-1 text-center py-4 text-sm text-muted-foreground">No payment data</div>
              )}
            </div>
            {/* Revenue metrics */}
            {revenueEfficiency && (
              <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-lg font-bold tabular-nums">{formatCurrency(revenueEfficiency.avgTicketSize)}</p>
                  <p className="text-xs text-muted-foreground">Avg Ticket</p>
                </div>
                <div>
                  <p className="text-lg font-bold tabular-nums">{formatCurrency(revenueEfficiency.revenuePerHour)}</p>
                  <p className="text-xs text-muted-foreground">Per Hour</p>
                </div>
                <div>
                  <p className="text-lg font-bold tabular-nums">{formatCurrency(revenueEfficiency.revenuePerChairHour)}</p>
                  <p className="text-xs text-muted-foreground">Per Chair</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==================== ROW 5: LOW STOCK (only if items exist) ==================== */}
      {lowStockItems.length > 0 && (
        <div className="card-bookdesk !p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-base font-semibold text-foreground">Low Stock Items</h3>
              </div>
              <span className="text-sm text-muted-foreground">{lowStockItems.length} items need restock</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-px bg-border">
            {lowStockItems.slice(0, 6).map(item => (
              <div key={item.id} className="bg-background p-4 hover:bg-muted/30 transition-colors">
                <p className="text-sm font-medium truncate">{item.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.category}</p>
                <p className="text-lg font-bold mt-2 tabular-nums">{item.quantity} / {item.minQuantity}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
